import React, { forwardRef, useCallback, useEffect, useRef } from "react"
import { filterDomProps } from "./dom-props"
import { flattenStyle } from "./style"

type ClickHandler = React.MouseEventHandler<HTMLElement>

/** RN onPress → React onClick，在组件边界显式转换，不做 styled 层拦截 */
function resolveClick(
  onClick?: ClickHandler,
  onPress?: ClickHandler,
): ClickHandler | undefined {
  return onClick ?? onPress
}

export type TouchButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "onClick"
> & {
  onPress?: ClickHandler
  onClick?: ClickHandler
  activeOpacity?: number
  hitSlop?: unknown
  testID?: string
}

/** styled.TouchableOpacity / styled.button 的 React 基座 */
export const TouchButton = forwardRef<HTMLButtonElement, TouchButtonProps>(function TouchButton(
  {
    onPress,
    onClick,
    activeOpacity: _activeOpacity,
    hitSlop: _hitSlop,
    testID,
    disabled,
    type,
    style,
    children,
    ...htmlProps
  },
  ref,
) {
  const handleClick = resolveClick(onClick, onPress)
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      disabled={disabled}
      data-testid={testID}
      style={flattenStyle(style as never)}
      onClick={disabled ? undefined : handleClick}
      {...filterDomProps(htmlProps as Record<string, unknown>)}
    >
      {children}
    </button>
  )
})

export type TouchableViewProps = Omit<React.HTMLAttributes<HTMLDivElement>, "onClick"> & {
  onPress?: ClickHandler
  onClick?: ClickHandler
  onLongPress?: () => void
  delayLongPress?: number
  testID?: string
}

/** styled.TouchableWithoutFeedback 的 React 基座 */
export const TouchableView = forwardRef<HTMLDivElement, TouchableViewProps>(function TouchableView(
  {
    onPress,
    onClick,
    onLongPress,
    delayLongPress,
    onContextMenu,
    testID,
    style,
    children,
    ...htmlProps
  },
  ref,
) {
  const handleClick = resolveClick(onClick, onPress)
  const longPressDelay = delayLongPress ?? 500
  const timerRef = useRef<number | null>(null)
  const longPressedRef = useRef(false)

  const clearLongPressTimer = useCallback(() => {
    if (timerRef.current != null && typeof window !== "undefined") {
      window.clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(() => {
    return () => clearLongPressTimer()
  }, [clearLongPressTimer])

  const startLongPressTimer = useCallback(() => {
    if (!onLongPress || typeof window === "undefined") return
    clearLongPressTimer()
    longPressedRef.current = false
    timerRef.current = window.setTimeout(() => {
      longPressedRef.current = true
      onLongPress()
      timerRef.current = null
    }, longPressDelay)
  }, [clearLongPressTimer, longPressDelay, onLongPress])

  return (
    <div
      ref={ref}
      data-testid={testID}
      style={flattenStyle(style as never)}
      role={handleClick || onLongPress ? "button" : undefined}
      tabIndex={handleClick || onLongPress ? 0 : undefined}
      onClick={(e) => {
        if (longPressedRef.current) {
          longPressedRef.current = false
          return
        }
        handleClick?.(e)
      }}
      onPointerDown={(e) => {
        if (e.pointerType === "mouse" && e.button !== 0) return
        startLongPressTimer()
      }}
      onPointerUp={() => {
        clearLongPressTimer()
      }}
      onPointerCancel={() => {
        clearLongPressTimer()
      }}
      onPointerLeave={() => {
        clearLongPressTimer()
      }}
      onContextMenu={
        onLongPress
          ? (e) => {
              e.preventDefault()
              onLongPress()
            }
          : onContextMenu
      }
      onKeyDown={
        handleClick
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                handleClick(e as unknown as React.MouseEvent<HTMLDivElement>)
              }
            }
          : undefined
      }
      {...filterDomProps(htmlProps as Record<string, unknown>)}
    >
      {children}
    </div>
  )
})
